import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16 relative z-10" data-testid="forgot-page">
      <div className="warm-card p-8">
        <h1 className="font-display text-3xl font-extrabold text-stone-900">Forgot your password?</h1>
        <p className="text-sm text-stone-500 mt-1">We'll email you a one-time link to choose a new one.</p>
        {sent ? (
          <p className="mt-6 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="forgot-sent">
            If an account exists for <span className="font-semibold">{email}</span>, a reset link is on its way. It expires in 1 hour — check spam too.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Email</label>
              <input type="email" required data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </div>
            <button type="submit" data-testid="forgot-submit" disabled={busy} className="amber-btn w-full disabled:opacity-50">{busy ? "Sending…" : "Send reset link"}</button>
          </form>
        )}
        <div className="text-sm text-stone-500 text-center mt-6">
          <Link to="/login" data-testid="forgot-back" className="text-amber-800 font-semibold hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password: pw });
      toast.success(r.data.message);
      navigate("/login");
    } catch (error) {
      setErr(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16 relative z-10" data-testid="reset-page">
      <div className="warm-card p-8">
        <h1 className="font-display text-3xl font-extrabold text-stone-900">Choose a new password</h1>
        {!token && <p className="mt-4 text-sm text-red-700" data-testid="reset-no-token">This link is missing its token. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p>}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">New password</label>
            <input type="password" required minLength={8} data-testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} />
            <div className="text-xs text-stone-500 mt-1">At least 8 characters.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Repeat it</label>
            <input type="password" required data-testid="reset-password-2" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} />
          </div>
          {err && <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
          <button type="submit" data-testid="reset-submit" disabled={busy || !token} className="amber-btn w-full disabled:opacity-50">{busy ? "Saving…" : "Save new password"}</button>
        </form>
      </div>
    </div>
  );
}
